/**
 * The tabs each workspace screen is showing right now, published by the screen.
 *
 * Which tabs a person sees is worked out inside the screens themselves (Org's
 * logic(), Board's PM check, MissionTabs' role rules), some of it from server
 * reads. mAX's navigate tool and context read it here rather than guessing it
 * again: a screen publishes {ids, active} under a scope ("home", "board",
 * "org:<id>", "mission:<id>") whenever its tabs or its active tab change, and
 * takes it back when it unmounts. Leaf module: it imports nothing.
 */

const published = new Map()
const listeners = new Set()

function notify() {
    listeners.forEach((fn) => {
        try {
            fn()
        } catch (err) {
            // A listener error must never break publishing
        }
    })
}

/**
 * @param scope {String} "home" | "board" | "org:<id>" | "mission:<id>"
 * @param tabs {Object}
 * @param tabs.ids {String[]} The tab ids the person can see, in screen order
 * @param [tabs.active] {String} The tab on screen
 * @return {function(): void} Unpublish, a no-op once published again
 */
export function publishScreenTabs(scope, { ids, active }) {
    const entry = { ids: (ids || []).filter(Boolean), active: active || null }

    published.set(scope, entry)
    notify()

    return () => {
        if (published.get(scope) === entry) {
            published.delete(scope)
            notify()
        }
    }
}

/**
 * For a reader that must follow the tab on screen (mAX keeps a conversation
 * per organisation tab), not just read it when asked.
 *
 * @param fn {function(): void} Called on every publish and unpublish
 * @return {function(): void} Unsubscribe
 */
export function subscribeScreenTabs(fn) {
    listeners.add(fn)

    return () => {
        listeners.delete(fn)
    }
}

/**
 * @param scope {String}
 * @return {{ids: String[], active: String|null}|null} Null when that screen is not mounted
 */
export function getScreenTabs(scope) {
    return published.get(scope) || null
}

/**
 * Resolves once the scope is published and passes the test, or with whatever is
 * published (possibly null) when the time runs out. For a caller that has just
 * navigated and wants to know where the screen landed.
 *
 * @param scope {String}
 * @param [test] {function({ids: String[], active: String|null}): Boolean}
 * @param [timeoutMs] {Number}
 * @return {Promise<{ids: String[], active: String|null}|null>}
 */
export function waitForScreenTabs(scope, test = () => true, timeoutMs = 6000) {
    const started = Date.now()

    return new Promise((resolve) => {
        const poll = () => {
            const tabs = getScreenTabs(scope)

            if ((tabs && test(tabs)) || Date.now() - started >= timeoutMs) {
                resolve(tabs)
            } else {
                setTimeout(poll, 150)
            }
        }

        poll()
    })
}

/**
 * Test helper.
 *
 * @return {void}
 */
export function clearScreenTabs() {
    published.clear()
}
